import UserService from '@/modules/user/services/userService';
import { User } from '@/modules/user/entities/user';
import { useHistoryNavigator } from '@/navigation';
import {
  authLoginMessageErrorByMessageErrorRequest,
  authLoginService,
} from '@/services/accessControl/endpoints/authLogin';
import { USER_TYPE } from '@/services/users/endpoints/users';
import { decodedJwtToken } from '@/utils/authentication';
import { useEffect, useRef, useState } from 'react';

import { AUTH_STEP } from '@/modules/authentication/types/authSteps.enum';
import { classifySessionError } from '@/modules/authentication/utils/classifySessionError';
import { commitSession } from '@/modules/authentication/utils/commitSession';
import { reportLoginFailure } from '@/modules/authentication/utils/reportLoginFailure';
import {
  SessionCompletion,
  createSessionCompletion,
} from '@/modules/authentication/utils/sessionCompletion';
import {
  AuthLoginTotp,
  authLoginTotpInitState,
  authLoginTotpService,
} from '@/services/accessControl/endpoints/authLoginTotp';
import { telemetryIdentifyUser } from '@/shared/telemetry/telemetry';
import { getMessageError } from '@/utils/getMessageError';
import { get } from 'lodash';
import Mixpanel from 'mixpanel-browser';

interface IUseAuthLogin {
  isCodeValid: (code: string) => string | null;
  login: (callback?: () => void) => Promise<void>;
  retrySession: (callback?: () => void) => Promise<void>;
  loginCheck: () => Promise<void>;
  setCurrentStep: (step: `${AUTH_STEP}`) => void;
  setData: Function;
  setMessageError: (messageError: string) => void;
  useData: AuthLoginTotp;
  useLoading: boolean;
  useMessageError: string;
  useCurrentStep: `${AUTH_STEP}`;
  sessionPending: boolean;
}

export interface JwtUser {
  id: string;
  name: string;
  email: string;
  type: USER_TYPE;
  permissions: string[];
  scopeIds: string[];
}

export interface JwtPayload {
  sub: string;
  user: JwtUser;
}

const SIZE_CODE = 6;

// Timeout específico do bootstrap do login; não altera o timeout global.
const BOOTSTRAP_TIMEOUT_IN_MILLISECONDS = 15_000;

const useAuthLogin = (): IUseAuthLogin => {
  const [useData, setData] = useState<AuthLoginTotp>(authLoginTotpInitState);
  const [useLoading, setLoading] = useState(false);
  const [useMessageError, setMessageError] = useState('');
  const [useCurrentStep, setCurrentStep] = useState<`${AUTH_STEP}`>(AUTH_STEP.EMAIL_PASSWORD);
  const [sessionPending, setSessionPending] = useState(false);
  const navigate = useHistoryNavigator();

  // Guarda contra cliques concorrentes (envio de OTP, retry, troca de conta).
  const operationInFlight = useRef(false);

  // Criado uma única vez por montagem; não recriar a cada render.
  const sessionCompletionRef = useRef<SessionCompletion | undefined>(undefined);
  if (!sessionCompletionRef.current) {
    sessionCompletionRef.current = createSessionCompletion({
      loadMe: (token) =>
        new UserService().getMe({
          token,
          timeoutMs: BOOTSTRAP_TIMEOUT_IN_MILLISECONDS,
          skipUnauthorizedRecovery: true,
        }) as Promise<User>,
      commitSession,
    });
  }

  const isCodeValid = (code: string): string | null => {
    if (code.length !== SIZE_CODE) {
      return 'Código inválido';
    }
    return null;
  };

  const extractUserFromToken = (token: string) => {
    const { user } = decodedJwtToken(token) as JwtPayload;
    return user;
  }

  const identifyUser = (user: JwtUser): void => {
    // Falha de telemetria não pode virar erro de login nem impedir navegação.
    try {
      telemetryIdentifyUser({
        id: user.id,
        name: user.name,
        email: user.email,
        type: user.type,
      });
      Mixpanel.identify(user.id);
      Mixpanel.register_once({ TYPE: user.type });
      Mixpanel.people.set_once({
        name: user.name,
        email: user.email,
        type: user.type,
      });
    } catch (error) {
      console.error('Falha de telemetria', { error })
    }
  };

  /**
   * Carrega a sessão (GET /me) e persiste. Separa sucesso funcional de
   * analytics e trata falhas do /me como sessão pendente (recuperável),
   * sem marcar o OTP como inválido.
   */
  const runSessionCompletion = async (
    token: string | null,
    callback?: () => void,
  ): Promise<void> => {
    const startedAt = Date.now();
    const sessionCompletion = sessionCompletionRef.current!;

    try {
      await sessionCompletion.complete();

      setSessionPending(false);
      setMessageError('');

      // Analytics é executado fora do catch dedicado ao /me.
      if (token) {
        const user = extractUserFromToken(token);
        identifyUser(user);
      }

      callback?.();
    } catch (error) {
      reportLoginFailure('session_load', error, Date.now() - startedAt);

      const kind = classifySessionError(error);

      if (kind === 'invalid_token') {
        // 401 confirmado / token expirado: reinicia a autenticação.
        sessionCompletion.clear();
        setSessionPending(false);
        setMessageError('Código invalido. Faça login novamente.');
        return;
      }

      if (kind === 'unauthorized') {
        // 401 confirmado / token expirado: reinicia a autenticação.
        sessionCompletion.clear();
        setSessionPending(false);
        setMessageError('Sua sessão expirou. Faça login novamente.');
        return;
      }

      if (kind === 'forbidden') {
        sessionCompletion.clear();
        setSessionPending(false);
        navigate.push(`/auth/blocked?otp=${encodeURIComponent(useData.email)}`);
        return;
      }

      if (kind === 'canceled') {
        // Cancelamento não é erro de código; mantém pendência silenciosa.
        setSessionPending(true);
        return;
      }

      // Timeout, sem resposta HTTP, 408/5xx/429, parse/JWT/storage, inesperado:
      // mantém o token pendente em memória e oferece retry manual.
      setSessionPending(true);
      setMessageError(
        'Seu código foi aceito, mas não foi possível carregar sua sessão. Tente novamente.',
      );
    }
  };

  const login = async (callback?: () => void): Promise<void> => {
    if (operationInFlight.current) {
      return;
    }
    operationInFlight.current = true;

    const startedAt = Date.now();
    const sessionCompletion = sessionCompletionRef.current!;

    try {
      setLoading(true);
      setMessageError('');
      setSessionPending(false);

      const { token } = (await authLoginTotpService.create(useData)) || {};
      if (!token) {
        return setMessageError('Falha ao fazer login.');
      }

      // OTP aceito: guarda o token pendente em memória e tenta carregar a sessão.
      sessionCompletion.setPendingToken(token);
      await runSessionCompletion(token, callback);
    } catch (error) {
      // Este catch trata apenas falhas da VALIDAÇÃO do OTP.
      reportLoginFailure('otp_validation', error, Date.now() - startedAt);

      const status: number = get(error, 'response.status', 401);
      const dataError = get(error, 'response.data', {
        attempt: null,
        message: 'Invalid credentials',
        statusCode: 401,
        totalAttempts: 3,
      });
      if (dataError?.attempt) {
        setMessageError(`Verifique o código de acesso em seu e-mail ou utilize a opção de reenviar código.
        `);
      } else if (status === 403) {
        navigate.push(`/auth/blocked?otp=${encodeURIComponent(useData.email)}`);
      } else {
        const messageError = getMessageError({
          error,
          messageErrorByMessageErrorRequest: authLoginMessageErrorByMessageErrorRequest,
          messageDefault: 'Falha ao verificar e-mail e senha.',
        });
        setMessageError(messageError);
      }
    } finally {
      setLoading(false);
      operationInFlight.current = false;
    }
  };

  /**
   * Retoma apenas o carregamento da sessão (GET /me). Não reenvia OTP nem
   * volta ao serviço de validação do código.
   */
  const retrySession = async (callback?: () => void): Promise<void> => {
    if (operationInFlight.current) {
      return;
    }
    if (!sessionCompletionRef.current?.hasPendingSession()) {
      return;
    }
    operationInFlight.current = true;

    try {
      setLoading(true);
      setMessageError('');
      await runSessionCompletion(null, callback);
    } finally {
      setLoading(false);
      operationInFlight.current = false;
    }
  };

  const loginCheck = async () => {
    setSessionPending(false)

    if (operationInFlight.current) {
      return;
    }
    operationInFlight.current = true;

    try {
      setLoading(true);
      setMessageError('');
      await authLoginService.create({
        email: useData.email,
        password: useData.password,
      });
      setCurrentStep(AUTH_STEP.OTP);
    } catch (error) {
      const status: number = get(error, 'response.status', 401);
      const details = get(error, 'response.data.details', { type: 'email' });
      const dataError = get(error, 'response.data', {
        attempt: null,
        message: 'Invalid credentials',
        statusCode: 401,
        totalAttempts: 3,
      });
      if (dataError?.attempt) {
        setMessageError(`E-mail e/ou senha inválidos. <br>
          Tentativa ${dataError.attempt} de ${dataError.totalAttempts}, caso tenha esquecido a senha, por favor, utilize o link Esqueceu sua senha!`);
      } else if (status === 403) {
        const query = details.type === 'mfa' ? 'otp' : 'email';
        navigate.push(`/auth/blocked?${query}=${encodeURIComponent(useData.email)}`);
      } else {
        const messageError = getMessageError({
          error,
          messageErrorByMessageErrorRequest: authLoginMessageErrorByMessageErrorRequest,
          messageDefault: 'Falha ao verificar e-mail e senha.',
        });
        setMessageError(messageError);
      }
    } finally {
      setLoading(false);
      operationInFlight.current = false;
    }
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  return {
    isCodeValid,
    login,
    retrySession,
    loginCheck,
    setCurrentStep,
    setData,
    setMessageError,
    useCurrentStep,
    useData,
    useLoading,
    useMessageError,
    sessionPending,
  };
};

export default useAuthLogin;
